import { ArrowLeft, Mail } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { showError, showSuccess } from "@/shared/utils/toast";
import { useMainStore } from "../stores";
import { requestAccess } from "@/api/auth";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import {
  getTargetRoute,
  handleReturnToSignUp,
} from "@/shared/utils/helperFunctions";

const AdminRequest = () => {
  const navigate = useNavigate();
  const { user, setUser } = useMainStore();
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRequestAccess = async () => {
    if (!user?.email) return;
    setIsSubmitting(true);
    try {
      await requestAccess({
        email: user.email,
        comment: message.trim() || "Please approve my workspace for SalesWorx.",
        target_role: "superadmin",
      });
      setUser({
        ...user,
        approvalStatus: "pending",
        hasAccessRequest: true,
        requestTarget: "superadmin",
        requestSubmittedAt: new Date().toISOString(),
      });
      navigate("/join-organization/status");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to send request. Please try again.";

      if (/already approved/i.test(msg)) {
        const updated = { ...user, approvalStatus: "approved" };
        setUser(updated);
        showSuccess("Your account is already approved!");
        setIsSubmitting(false);
        navigate(
          getTargetRoute(updated, {
            appMode: useMainStore.getState().appMode ?? "user",
          }),
        );
        return;
      }

      showError(msg);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F9FAFB] flex items-center justify-center h-[100dvh] w-full">
      <div className="flex items-start  gap-4  w-[90%]">
        <div
          className="flex items-center gap-1.5 cursor-pointer "
          onClick={() => handleReturnToSignUp(navigate)}
        >
          {" "}
          <ArrowLeft
            strokeWidth={1.5}
            size={20}
            className="text-secondary cursor-pointer"
          />
          <button className="text-secondary font-normal text-[0.85rem] hover:underline transition-all duration-300 w-fit">
            Return to Login
          </button>
        </div>
        <div
          className="border border-gray-200 rounded-2xl p-6 w-[47%]
        bg-white flex flex-col gap-[22px] shadow-sm max-w-[512px] mx-auto"
        >
          {/* icon */}
          <div className="flex items-center justify-center rounded-full p-3.5 bg-warning-50 w-fit">
            <Mail className="text-warning-500" size={22} />
          </div>

          {/* heading/para */}
          <div className="flex flex-col gap-2.5">
            <h1 className="text-gray-800 text-[1.38rem] leading-8 font-normal">
              Request Workspace Approval
            </h1>
            <p className="text-gray-500 text-[0.85rem] leading-5 font-normal max-w-[420px]">
              Your organization is new to SalesWorx. Request approval from the
              SalesWorx team to set up your workspace and start creating
              campaigns.
            </p>
          </div>

          {/* admin details */}
          <div className="flex flex-col text-secondary p-4 bg-[#207C971A] rounded-[10px] border border-secondary">
            <p className="text-[0.85rem] leading-5 font-normal">
              Your email: {user?.email}
            </p>
          </div>

          {/* message input */}
          <div className="flex flex-col gap-1.5">
            <label className="text-gray-800 text-[0.85rem] font-semibold">
              Message to SalesWorx (Optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g., I'm setting up SalesWorx for my team and would like to get started."
              rows={3}
              maxLength={200}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-[0.85rem] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-secondary resize-none transition-all duration-200"
            />
            <p className="text-gray-400 text-[0.7rem] text-right">
              {message.length}/200
            </p>
          </div>

          <PrimaryButton
            children="Request Approval"
            startContent={false}
            onClick={handleRequestAccess}
            isLoading={isSubmitting}
            disabled={isSubmitting}
            className="w-full"
          />
          {/* divider */}
          <div className="h-[0.5px] bg-gray-200" />
          {/* need help info */}
          <p className="text-gray-500 text-[0.7rem] text-center leading-[18px] font-normal">
            Need help?{" "}
            <span className="text-secondary cursor-pointer hover:underline transition-all duration-300">
              Contact Support
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminRequest;
